import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../api/client";
import toast from "react-hot-toast";
import {
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  BanknotesIcon,
  UsersIcon,
  CurrencyEuroIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline";
import type { AxiosError } from "axios";

interface ApiErrorResponse {
  message?: string;
}

interface BankTransferData {
  userId: string;
  customerNumber: number;
  name: string;
  iban: string;
  bic?: string;
  amount: number;
  reference: string;
  purpose: string;
  contractCompletedDate?: string;
  email: string;
}


export default function BankTransferExports() {
  const [filters, setFilters] = useState({
    status: "scheduled" as "executed" | "scheduled" | "all",
    fromDate: "",
    toDate: "",
    format: "sepa" as "sepa" | "german",
    includeZeroAmounts: false,
  });

  const [showPreview, setShowPreview] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isConfirming, setIsConfirming] = useState(false);

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ["bankTransferStats"],
    queryFn: async () => {
      const res = await api.get("/admin/exports/bank-transfers/stats");
      return res.data.data;
    },
  });

  // Fetch preview data
  const {
    data: previewData,
    isLoading: previewLoading,
    refetch: refetchPreview,
  } = useQuery({
    queryKey: ["bankTransferPreview", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.status) params.append("status", filters.status);
      if (filters.fromDate) params.append("fromDate", filters.fromDate);
      if (filters.toDate) params.append("toDate", filters.toDate);
      if (filters.format) params.append("format", filters.format);
      if (filters.includeZeroAmounts)
        params.append("includeZeroAmounts", "true");

      const res = await api.get(
        `/admin/exports/bank-transfers/preview?${params.toString()}`
      );
      return res.data.data;
    },
  });

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append("status", filters.status);
      if (filters.fromDate) params.append("fromDate", filters.fromDate);
      if (filters.toDate) params.append("toDate", filters.toDate);
      if (filters.format) params.append("format", filters.format);
      if (filters.includeZeroAmounts)
        params.append("includeZeroAmounts", "true");

      const response = await api.get(
        `/admin/exports/bank-transfers?${params.toString()}`,
        {
          responseType: "blob",
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      const filename = `bank-transfers-${new Date().toISOString().split("T")[0]}.csv`;
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("CSV file downloaded successfully");
       
    } catch (error) {
      console.error("Export error:", error);
      toast.error((error as AxiosError<ApiErrorResponse>).response?.data?.message || "Failed to export CSV");
    } finally {
      setIsExporting(false);
    }
  };

  const handlePreview = () => {
    setShowPreview(true);
    refetchPreview();
    setSelectedIds([]); // Reset selection on new preview
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked && previewData?.transfers) {
      const allIds = previewData.transfers.map((t: BankTransferData) => t.userId);
      setSelectedIds(allIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectRow = (userId: string) => {
    if (selectedIds.includes(userId)) {
      setSelectedIds(selectedIds.filter(id => id !== userId));
    } else {
      setSelectedIds([...selectedIds, userId]);
    }
  };

  const handleConfirmPayout = async () => {
    if (!window.confirm(`Are you sure you want to mark ${selectedIds.length} users as Payout Executed? This action cannot be easily undone via the UI.`)) {
      return;
    }

    setIsConfirming(true);
    try {
      await api.post("/admin/payouts/confirm", { userIds: selectedIds });
      toast.success(`Successfully confirmed payout for ${selectedIds.length} users`);
      refetchPreview(); // Refresh data
      setSelectedIds([]); // Clear selection
       
    } catch (error) {
      console.error("Payout confirmation error:", error);
      toast.error((error as AxiosError<ApiErrorResponse>).response?.data?.message || "Failed to confirm payouts");
    } finally {
      setIsConfirming(false);
    }
  };

  const statsCards = stats
    ? [
        {
          name: "Scheduled Payouts",
          value: stats.scheduled.count,
          amount: `€${stats.scheduled.totalAmount.toFixed(2)}`,
          icon: ExclamationTriangleIcon,
          color: "bg-yellow-500",
        },
        {
          name: "Executed Payouts",
          value: stats.executed.count,
          amount: `€${stats.executed.totalAmount.toFixed(2)}`,
          icon: CheckCircleIcon,
          color: "bg-green-500",
        },
      ]
    : [];

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Bank Transfer Exports
          </h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
            Generate CSV files for bank transfers to claimants who have signed
            contracts
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {statsCards.map((item) => (
            <div
              key={item.name}
              className="relative overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pt-5 pb-12 shadow sm:px-6 sm:pt-6"
            >
              <dt>
                <div className={`absolute rounded-md p-3 ${item.color}`}>
                  <item.icon className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                <p className="ml-16 truncate text-sm font-medium text-gray-500 dark:text-gray-400">
                  {item.name}
                </p>
              </dt>
              <dd className="ml-16 pb-6 sm:pb-7">
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {item.value}
                </p>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mt-1">
                  {item.amount}
                </p>
              </dd>
            </div>
          ))}
        </div>
      )}

      {/* Filter Panel */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Export Filters
        </h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {/* Status Filter */}
          <div>
            <label
              htmlFor="status"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Payout Status
            </label>
            <select
              id="status"
              value={filters.status}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  status: e.target.value as "executed" | "scheduled" | "all",
                })
              }
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="scheduled">Scheduled</option>
              <option value="executed">Executed</option>
              <option value="all">All</option>
            </select>
          </div>

          {/* Format Filter */}
          <div>
            <label
              htmlFor="format"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              CSV Format
            </label>
            <select
              id="format"
              value={filters.format}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  format: e.target.value as "sepa" | "german",
                })
              }
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="sepa">SEPA Standard</option>
              <option value="german">German Bank (DTA)</option>
            </select>
          </div>

          {/* From Date */}
          <div>
            <label
              htmlFor="fromDate"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              From Date
            </label>
            <input
              type="date"
              id="fromDate"
              value={filters.fromDate}
              onChange={(e) =>
                setFilters({ ...filters, fromDate: e.target.value })
              }
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>

          {/* To Date */}
          <div>
            <label
              htmlFor="toDate"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              To Date
            </label>
            <input
              type="date"
              id="toDate"
              value={filters.toDate}
              onChange={(e) =>
                setFilters({ ...filters, toDate: e.target.value })
              }
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>

          {/* Include Zero Amounts */}
          <div className="flex items-center col-span-full">
            <input
              id="includeZeroAmounts"
              type="checkbox"
              checked={filters.includeZeroAmounts}
              onChange={(e) =>
                setFilters({ ...filters, includeZeroAmounts: e.target.checked })
              }
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <label
              htmlFor="includeZeroAmounts"
              className="ml-2 block text-sm text-gray-700 dark:text-gray-300"
            >
              Include users with zero payout amounts
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={handlePreview}
            disabled={previewLoading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <UsersIcon className="h-5 w-5 mr-2" />
            {previewLoading ? "Loading..." : "Preview Data"}
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={isExporting}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
            {isExporting ? "Generating..." : "Download CSV"}
          </button>
        </div>
      </div>

      {/* Preview Panel */}
      {showPreview && previewData && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Export Preview
            </h3>
            {selectedIds.length > 0 && (
              <button
                type="button"
                onClick={handleConfirmPayout}
                disabled={isConfirming}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
              >
                {isConfirming ? "Processing..." : `Confirm Payout (${selectedIds.length})`}
              </button>
            )}
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 mb-6">
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center">
                <UsersIcon className="h-8 w-8 text-indigo-600 dark:text-indigo-400" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Total Records
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {previewData.summary.recordCount}
                  </p>
                </div>
              </div>
            </div>
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center">
                <CurrencyEuroIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Total Amount
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    €{previewData.summary.totalAmount.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center">
                <BanknotesIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Average Amount
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    €{previewData.summary.averageAmount.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Validation Results */}
          {previewData.validation && (
            <div className="mb-6">
              <div className="flex items-center mb-2">
                {previewData.validation.valid ? (
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                ) : (
                  <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                )}
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                  Validation Status:{" "}
                  {previewData.validation.valid ? "Passed" : "Failed"}
                </h4>
              </div>

              {/* Errors */}
              {previewData.validation.errors.length > 0 && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4 mb-3">
                  <h5 className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">
                    Errors ({previewData.validation.errors.length})
                  </h5>
                  <ul className="list-disc list-inside space-y-1 text-sm text-red-700 dark:text-red-300">
                    {previewData.validation.errors.slice(0, 10).map((error: string, idx: number) => (
                      <li key={idx}>{error}</li>
                    ))}
                    {previewData.validation.errors.length > 10 && (
                      <li>
                        ... and {previewData.validation.errors.length - 10} more
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {/* Warnings */}
              {previewData.validation.warnings.length > 0 && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-4">
                  <h5 className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                    Warnings ({previewData.validation.warnings.length})
                  </h5>
                  <ul className="list-disc list-inside space-y-1 text-sm text-yellow-700 dark:text-yellow-300">
                    {previewData.validation.warnings
                      .slice(0, 10)
                      .map((warning: string, idx: number) => (
                        <li key={idx}>{warning}</li>
                      ))}
                    {previewData.validation.warnings.length > 10 && (
                      <li>
                        ... and {previewData.validation.warnings.length - 10} more
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Data Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th scope="col" className="relative w-12 px-6 sm:w-16 sm:px-8">
                    <input
                      type="checkbox"
                      className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 sm:left-6"
                      onChange={handleSelectAll}
                      checked={
                        previewData.transfers.length > 0 &&
                        selectedIds.length === previewData.transfers.length
                      }
                    />
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Customer #
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    IBAN
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Reference
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Email
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {previewData.transfers.slice(0, 20).map((transfer: BankTransferData) => {
                  const CopyButton = ({ value, label }: { value: string; label: string }) => (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(value);
                        toast.success(`${label} copied`);
                      }}
                      className="ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                      title={`Copy ${label}`}
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" />
                    </button>
                  );

                  return (
                    <tr key={transfer.userId} className={selectedIds.includes(transfer.userId) ? "bg-gray-50 dark:bg-gray-700" : ""}>
                      <td className="relative w-12 px-6 sm:w-16 sm:px-8">
                        {selectedIds.includes(transfer.userId) && (
                          <div className="absolute inset-y-0 left-0 w-0.5 bg-indigo-600" />
                        )}
                        <input
                          type="checkbox"
                          className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 sm:left-6"
                          checked={selectedIds.includes(transfer.userId)}
                          onChange={() => handleSelectRow(transfer.userId)}
                        />
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        <span className="inline-flex items-center">
                          {transfer.customerNumber}
                          <CopyButton value={String(transfer.customerNumber)} label="Customer #" />
                        </span>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        <span className="inline-flex items-center">
                          {transfer.name}
                          <CopyButton value={transfer.name} label="Name" />
                        </span>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm font-mono text-gray-900 dark:text-white">
                        <span className="inline-flex items-center">
                          {transfer.iban}
                          <CopyButton value={transfer.iban} label="IBAN" />
                        </span>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        <span className="inline-flex items-center">
                          €{transfer.amount.toFixed(2)}
                          <CopyButton value={transfer.amount.toFixed(2)} label="Amount" />
                        </span>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        <span className="inline-flex items-center">
                          {transfer.reference}
                          <CopyButton value={transfer.reference} label="Reference" />
                        </span>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        <span className="inline-flex items-center">
                          {transfer.email}
                          <CopyButton value={transfer.email} label="Email" />
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {previewData.transfers.length > 20 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                Showing first 20 of {previewData.transfers.length} records
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
